/**
 * InstanceDeleteDialog — Confirmation dialog for deleting a pending instance.
 *
 * Only pending instances can be deleted (not in_progress, completed, etc.).
 * The caller should check preconditions before opening this dialog.
 */

import { useLanguage } from '@/core/LanguageContext';
import { useDeleteInstance } from '../hooks/useScheduleInstances';
import type { LpaScheduleInstanceWithDetails } from '../types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InstanceDeleteDialogProps {
  instance: LpaScheduleInstanceWithDetails | null;
  onClose: () => void;
  auditId: string;
}

export default function InstanceDeleteDialog({
  instance,
  onClose,
  auditId,
}: InstanceDeleteDialogProps) {
  const { t } = useLanguage();
  const deleteInstance = useDeleteInstance();

  const handleDelete = () => {
    if (!instance) return;
    deleteInstance.mutate(
      {
        instanceId: instance.id,
        auditId,
        scheduleId: instance.schedule_id,
      },
      { onSuccess: () => onClose() },
    );
  };

  const periodLabel = instance
    ? `${new Date(instance.period_start).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })}–${new Date(instance.period_end).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })}`
    : '';

  return (
    <AlertDialog open={!!instance} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('mod_lpa.instances.deleteDialog.title', 'Delete instance?')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              'mod_lpa.instances.deleteDialog.description',
              'This will permanently delete the audit instance for "{schedule}" ({period}). This action cannot be undone.',
            )
              .replace('{schedule}', instance?.schedule.name ?? '')
              .replace('{period}', periodLabel)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteInstance.isPending}>
            {t('mod_lpa.instances.deleteDialog.cancel', 'Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteInstance.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {t('mod_lpa.instances.deleteDialog.confirm', 'Delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
